import { useCallback, useState } from "react";
import { evaluate } from "mathjs";
import { useFormulas } from "../../../hooks/query";

// Define measurements type based on actual usage (array of objects)
type MeasurementValues = any[];

interface PrefilledValues {
    [product: string]: {
        value: string;
        quantity?: number;
    };
}

const useFormulaEvaluator = () => {
    const [computedValues, setComputedValues] = useState<Record<string, number | null>>({});
    const { useList } = useFormulas();
    const { data: formulas, isLoading: isLoadingFormulas } = useList();

    const evaluateFormula = useCallback(
        (measurements: MeasurementValues, prefilledValues: PrefilledValues): Record<string, number | null> => {
            const results: Record<string, number | null> = {};

            // Return empty results if formulas are not loaded yet
            if (!formulas || isLoadingFormulas) {
                return results;
            }

            for (const product in formulas) {
                const productFormula = formulas[product];

                let expression: string | undefined;
                let outputFormat: "WholeNumber" | "Decimal" | undefined;

                // Check if product has subcategories
                if (productFormula.subcategories) {
                    const selectedSubcategory = prefilledValues[product]?.value;
                    if (selectedSubcategory && productFormula.subcategories[selectedSubcategory]) {
                        const subcategoryFormula = productFormula.subcategories[selectedSubcategory];
                        expression = subcategoryFormula.expression;
                        outputFormat = subcategoryFormula.outputFormat;
                    }
                }

                // If no subcategory formula was found, use the product-level formula
                if (!expression && productFormula.expression) {
                    expression = productFormula.expression;
                    outputFormat = productFormula.outputFormat;
                }

                if (expression) {
                    try {
                        const measurementsObject = convertMeasurementIntoKeyValuePair(measurements);
                        
                        // Inject Selections into scope (e.g. FieldShingles_Selection = "Tamko Heritage")
                        const selectionsScope: Record<string, any> = {};
                        Object.entries(prefilledValues).forEach(([prod, data]) => {
                            const normalizedName = prod.replace(/\s+/g, "") + "_Selection";
                            // Wrap in quotes to ensure mathjs treats it as a string literal
                            selectionsScope[normalizedName] = data.value
                        });

                        // Inject Calculated Results into scope (e.g. FieldShingles = 123)
                        const resultsScope: Record<string, any> = {};
                        Object.entries(results).forEach(([prod, val]) => {
                             if (val !== null) {
                                const normalizedName = prod.replace(/\s+/g, "");
                                resultsScope[normalizedName] = val;
                             }
                        });
                        
                        const scope = { ...measurementsObject, ...selectionsScope, ...resultsScope };
                        

                        let result = evaluate(expression, scope) as number;
                        console.log(expression, result);
                        // Apply output formatting
                        if (outputFormat === "WholeNumber") {
                            result = Math.ceil(result);
                        }

                        results[product] = result;
                    } catch (error: any) {
                        // Only log real errors, not missing data (undefined symbols)
                        if (error && error.message && error.message.includes("Undefined symbol")) {
                            // console.debug(`Missing measurement for ${product}:`, error.message);
                        } else {
                            console.error(`Error evaluating formula for ${product}:`, error);
                        }
                        results[product] = null;
                    }
                }
            }

            setComputedValues(results);
            return results; // Return computed values immediately
        },
        [formulas, isLoadingFormulas]
    );

    const convertMeasurementIntoKeyValuePair = (measurements: any) => {
        return measurements.reduce((acc: any, measurement: any) => {
            // Use ActualValue if available, otherwise fall back to CalculatedValue (predicted value)
            const value = measurement.ActualValue ?? measurement.CalculatedValue;
            
            // Try updating to number, but if it fails (NaN), keep as original string (unless it is empty/null)
            const numVal = Number(value);
            if (!isNaN(numVal) && value !== "" && value !== null && value !== undefined) {
                 acc[measurement.MeasurementType] = numVal;
            } else {
                 acc[measurement.MeasurementType] = value;
            }
            return acc;
        }, {});
    };

    return { computedValues, evaluateFormula };
};

export default useFormulaEvaluator;
